// The palette's list, flattened, and its keyboard — TODO T36 (2 Sep 2026).
//
// Pure, because the keyboard is the part of a palette that goes wrong invisibly: an arrow that
// skips a group heading, an Enter that opens the wrong row, a selection that survives a new
// answer and points at nothing. The component (Palette.tsx) renders what this returns and
// forwards the keys.
import type { PaletteResponse } from '@vera/bff';

export interface PaletteItem {
  /**
   * Stable within one answer: `candidate:<id>`, `search:<id>`, `client:<id>`, `connection:<id>`,
   * `list:<q>`, `ask:<q>`.
   */
  key: string;
  group: 'Candidates' | 'Searches' | 'Clients' | 'Connections' | 'More' | 'Ask kadmon';
  title: string;
  sub: string;
  /** Where Enter goes. */
  to: string;
  /** A candidate row draws an avatar; the others draw an icon. */
  avatar: string | null;
  /** T35: the ask row carries this so the drawer asks the sentence as it opens. */
  state?: { ask: true };
  /** T35: the ask row on a server with no key — drawn, says why, opens nothing. */
  disabled?: boolean;
}

const STATUS_LABEL: Record<PaletteResponse['searches'][number]['status'], string> = {
  sourcing: 'Sourcing',
  active: 'Active',
  shortlist: 'Shortlist',
  client_review: 'Client review',
  closed: 'Closed',
};

/**
 * Whether what was typed reads as a question for the find rather than a name for the lookup —
 * T35 (3 Sep 2026). A name or an employer is a word or two; a need is a sentence. Four words,
 * forty characters or a question mark, and Enter opens the find panel instead of the first row.
 */
export function isSentence(q: string): boolean {
  const term = q.trim();
  if (term === '') return false;
  if (term.endsWith('?')) return true;
  return term.split(/\s+/).length >= 4 || term.length >= 40;
}

export const ASK_HINT =
  'Find people or have her act: “a CFO in Stockholm”, “move Northwind to Clients”, “pause Anna’s thread”';

/**
 * The row that opens the find panel (T35). Its `to` is a query string alone, so it opens the
 * drawer over whatever screen the palette was opened on; the search page's own button does the
 * same with `?find=brief`. With no key on the server the row is drawn and says so.
 */
export function askItem(term: string, findEnabled: boolean): PaletteItem {
  return {
    key: `ask:${term}`,
    group: 'Ask kadmon',
    title: findEnabled ? 'Ask kadmon' : 'Ask kadmon (not set up on this server)',
    sub: term ? `“${term}”` : ASK_HINT,
    to: `?find=${encodeURIComponent(term || '1')}`,
    avatar: null,
    ...(term ? { state: { ask: true as const } } : {}),
    ...(findEnabled ? {} : { disabled: true }),
  };
}

/**
 * One list in the order the groups are drawn, with the candidates-list fallback last: the
 * palette shows a handful of each group, and "everyone matching this" is the candidates screen's
 * job — which already takes `?q=` (screens/CandidatesScreen.tsx).
 *
 * The ask row (T35) is FIRST when the term reads as a sentence and the server can find — Enter
 * then opens the panel with it — and last otherwise, where the mockup draws it.
 */
export function flattenPalette(
  result: PaletteResponse | undefined,
  q: string,
  findEnabled = true,
): PaletteItem[] {
  const term = q.trim();
  if (!result || term === '') return [];
  const items: PaletteItem[] = [];
  const ask = askItem(term, findEnabled);
  const askFirst = findEnabled && isSentence(term);
  if (askFirst) items.push(ask);
  for (const c of result.candidates) {
    items.push({
      key: `candidate:${c.id}`,
      group: 'Candidates',
      title: c.fullName ?? c.e164 ?? 'Unnamed',
      sub:
        [c.currentTitle, c.currentEmployer, c.location].filter(Boolean).join(' · ') ||
        (c.e164 ?? 'no number yet'),
      to: `/candidates/${c.id}`,
      avatar: c.fullName ?? c.e164 ?? '?',
    });
  }
  for (const s of result.searches) {
    items.push({
      key: `search:${s.id}`,
      group: 'Searches',
      title: s.title,
      sub: [s.clientName ?? 'No client on record', STATUS_LABEL[s.status]].join(' · '),
      to: `/searches/${s.id}`,
      avatar: null,
    });
  }
  for (const c of result.clients) {
    items.push({
      key: `client:${c.id}`,
      group: 'Clients',
      title: c.name,
      // The tab it is on (T58): a prospect is not a client yet, and the palette should not say so.
      sub:
        c.status === 'client' ? 'Client' : c.status === 'potential' ? 'Potential client' : 'Sales',
      to: `/crm/clients/${c.id}`,
      avatar: null,
    });
  }
  // T111: somebody in the team's LinkedIn networks, labelled a connection — not a candidate, not a
  // contact — and opened in place on the Connections tab.
  for (const c of result.connections) {
    const job = c.title && c.company ? `${c.title} at ${c.company}` : (c.title ?? c.company);
    items.push({
      key: `connection:${c.id}`,
      group: 'Connections',
      title: c.fullName,
      sub: ['Connection', job].filter(Boolean).join(' · '),
      to: `/crm/connections?connection=${c.id}`,
      avatar: c.fullName,
    });
  }
  items.push({
    key: `list:${term}`,
    group: 'More',
    title: `Search the candidates list for “${term}”`,
    sub: 'Name, employer or number: everyone who matches',
    to: `/candidates?q=${encodeURIComponent(term)}`,
    avatar: null,
  });
  if (!askFirst) items.push(ask);
  return items;
}

/** Arrow keys wrap; a selection past the end of a shorter new answer snaps back to the last row. */
export function moveSelection(index: number, delta: -1 | 1, length: number): number {
  if (length === 0) return 0;
  return (((index + delta) % length) + length) % length;
}

export function clampSelection(index: number, length: number): number {
  if (length === 0) return 0;
  return Math.min(Math.max(index, 0), length - 1);
}
